import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';

/**
 * Texts tab on the event editor (Evan 2026-10-30: "Build text blast control in my event page as
 * an additional tab. i should be able to choose who to send notifications to, when, and what
 * exactly"). Writes a row to shado_event_blasts (events_v28); the event-blast edge fn sends it —
 * right away for "Send now", or on its scheduled minute via the event-blast-due cron.
 */

const inputCls =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-white placeholder:text-mutedDim focus:border-accent focus:outline-none';
const labelCls = 'mb-1 block text-[10.5px] uppercase tracking-[0.08em] text-mutedDim';

type Audience = 'all' | 'list' | 'buyers' | 'checked_in' | 'not_checked_in' | 'pending' | 'custom';

const AUDIENCE_LABELS: [Audience, string, string][] = [
  ['all', 'Everyone', 'Guest list + anyone who bought'],
  ['list', 'Guest list', 'Everyone who claimed a ticket'],
  ['buyers', 'Buyers', 'Paid tickets + merch orders'],
  ['checked_in', 'Checked in', 'Already through the door'],
  ['not_checked_in', 'Not checked in', 'On the list, not here yet'],
  ['pending', 'Pending claims', 'Verified but short of the ratings'],
  ['custom', 'Pick people', 'Choose exactly who'],
];

export type TextGuest = { name: string; phone: string | null; tag: string };

type Blast = {
  id: string;
  audience: Audience;
  phones: string[] | null;
  message: string;
  send_at: string;
  status: 'scheduled' | 'sending' | 'sent' | 'failed' | 'canceled';
  recipient_count: number | null;
  sent_count: number | null;
  failed_count: number | null;
  error: string | null;
  created_at: string;
};

function d10(p: string | null): string {
  return (p ?? '').replace(/\D/g, '').slice(-10);
}

/** datetime-local value ↔ ISO, in the browser's local zone. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventTextsTab({ eventId, guests }: { eventId: string; guests: TextGuest[] }) {
  const [audience, setAudience] = useState<Audience>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pickQuery, setPickQuery] = useState('');
  const [message, setMessage] = useState('');
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [sendAt, setSendAt] = useState(() => toLocalInput(new Date(Date.now() + 60 * 60 * 1000)));
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [sender, setSender] = useState<{ status: string; reason?: string | null } | null>(null);
  const [blasts, setBlasts] = useState<Blast[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // One row per distinct phone (a guest who claimed AND bought shows once).
  const people = useMemo(() => {
    const seen = new Map<string, TextGuest>();
    for (const g of guests) {
      const k = d10(g.phone);
      if (k.length !== 10 || seen.has(k)) continue;
      seen.set(k, g);
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [guests]);

  const loadBlasts = useCallback(async () => {
    const { data } = await supabase
      .from('shado_event_blasts')
      .select('id, audience, phones, message, send_at, status, recipient_count, sent_count, failed_count, error, created_at')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false })
      .limit(50);
    setBlasts((data ?? []) as Blast[]);
  }, [eventId]);

  const loadCounts = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('event-blast', {
      body: { counts: true, event_id: eventId },
    });
    if (!error) {
      const d = data as { counts?: Record<string, number>; sender?: { status: string; reason?: string | null } | null };
      setCounts(d?.counts ?? null);
      setSender(d?.sender ?? null);
    }
  }, [eventId]);

  useEffect(() => {
    void loadBlasts();
    void loadCounts();
  }, [loadBlasts, loadCounts]);

  // While anything is scheduled or mid-send, refresh the history every 20s so it flips to Sent.
  useEffect(() => {
    if (!blasts?.some((b) => b.status === 'scheduled' || b.status === 'sending')) return;
    const t = window.setInterval(() => void loadBlasts(), 20_000);
    return () => window.clearInterval(t);
  }, [blasts, loadBlasts]);

  const recipientCount = audience === 'custom' ? picked.size : counts?.[audience] ?? null;
  const finalText = /reply stop/i.test(message) ? message.trim() : `${message.trim()}\n\nReply STOP to opt out.`;
  // GSM-7 segments are 160 chars (153 when split); an emoji flips to UCS-2 (70/67).
  const ucs2 = Array.from(finalText).some((ch) => ch.charCodeAt(0) > 127);
  const segments = finalText.length <= (ucs2 ? 70 : 160) ? 1 : Math.ceil(finalText.length / (ucs2 ? 67 : 153));

  const submit = async () => {
    setNotice(null);
    const msg = message.trim();
    if (msg.length < 2) return setNotice('Write the message first.');
    if (audience === 'custom' && picked.size === 0) return setNotice('Pick at least one person.');
    let sendAtIso = new Date().toISOString();
    if (when === 'later') {
      const d = new Date(sendAt);
      if (Number.isNaN(d.getTime()) || d.getTime() < Date.now() + 60_000) {
        return setNotice('Pick a time at least a minute from now.');
      }
      sendAtIso = d.toISOString();
    }
    const who = AUDIENCE_LABELS.find((a) => a[0] === audience)?.[1] ?? audience;
    const whenText =
      when === 'now'
        ? 'right now'
        : `at ${new Date(sendAtIso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
    if (
      !window.confirm(
        `Text ${recipientCount ?? '?'} ${recipientCount === 1 ? 'person' : 'people'} (${who}) ${whenText}?\n\n${finalText}`,
      )
    ) {
      return;
    }
    setBusy(true);
    const phones =
      audience === 'custom'
        ? people.filter((p) => picked.has(d10(p.phone))).map((p) => `+1${d10(p.phone)}`)
        : null;
    const { data: row, error } = await supabase
      .from('shado_event_blasts')
      .insert({ event_id: eventId, audience, phones, message: msg, send_at: sendAtIso })
      .select('id')
      .single();
    if (error || !row) {
      setBusy(false);
      return setNotice(error?.message ?? 'Could not save the text.');
    }
    if (when === 'now') {
      const { data, error: e } = await supabase.functions.invoke('event-blast', {
        body: { blast_id: (row as { id: string }).id },
      });
      if (e) {
        let m = e.message;
        try {
          const ctx = (e as { context?: Response }).context;
          if (ctx) m = ((await ctx.json()) as { error?: string }).error ?? m;
        } catch {
          /* keep generic */
        }
        setNotice(m);
      } else {
        const d = data as { sent?: number; failed?: number; recipients?: number; error?: string | null };
        setNotice(
          `Sent to ${d.sent ?? 0} of ${d.recipients ?? 0}${d.failed ? ` — ${d.failed} failed (${d.error ?? 'see history'})` : ''}.`,
        );
        if (!d.failed) setMessage('');
      }
    } else {
      setNotice(`Scheduled — it goes out ${whenText}.`);
      setMessage('');
    }
    setBusy(false);
    void loadBlasts();
  };

  const cancel = async (b: Blast) => {
    if (!window.confirm('Cancel this scheduled text?')) return;
    const { error } = await supabase
      .from('shado_event_blasts')
      .update({ status: 'canceled' })
      .eq('id', b.id)
      .eq('status', 'scheduled');
    if (error) setNotice(error.message);
    void loadBlasts();
  };

  const filteredPeople = people.filter((p) => {
    const q = pickQuery.trim().toLowerCase();
    if (!q) return true;
    return p.name.toLowerCase().includes(q) || d10(p.phone).includes(q.replace(/\D/g, '') || '\u0000');
  });

  return (
    <>
      <div className="admin-card space-y-4">
        <div className="admin-eyebrow">New text</div>

        {/* Texting-number verification (Twilio toll-free). Unverified = carriers block delivery. */}
        {sender && (
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              sender.status === 'TWILIO_APPROVED'
                ? 'bg-green-500/10 text-green-400'
                : sender.status === 'TWILIO_REJECTED'
                  ? 'bg-red-500/10 text-red-400'
                  : 'bg-yellow-500/10 text-yellow-300'
            }`}
          >
            {sender.status === 'TWILIO_APPROVED'
              ? 'Texting number approved — texts will deliver.'
              : sender.status === 'TWILIO_REJECTED'
                ? `Texting number rejected${sender.reason ? `: ${sender.reason}` : ''}. Texts won't deliver until it's fixed and resubmitted.`
                : sender.status === 'PENDING_REVIEW' || sender.status === 'IN_REVIEW'
                  ? 'Texting number is still waiting on carrier approval — texts won\'t deliver yet. You can schedule them for later.'
                  : `Texting number status: ${sender.status}`}
          </div>
        )}

        {/* WHO */}
        <div>
          <label className={labelCls}>Who</label>
          <div className="flex flex-wrap gap-2">
            {AUDIENCE_LABELS.map(([k, label, hint]) => {
              const n = k === 'custom' ? picked.size : counts?.[k];
              return (
                <button
                  key={k}
                  type="button"
                  title={hint}
                  onClick={() => setAudience(k)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-medium ${
                    audience === k
                      ? 'border-accent bg-accent/15 text-accent'
                      : 'border-border text-mutedDim hover:text-white'
                  }`}
                >
                  {label}
                  {n !== undefined ? <span className="ml-1.5 tabular-nums opacity-70">{n}</span> : null}
                </button>
              );
            })}
          </div>
          <div className="mt-1.5 text-[11px] text-mutedDim">
            {AUDIENCE_LABELS.find((a) => a[0] === audience)?.[2]}
          </div>
          {audience === 'custom' ? (
            <div className="mt-2 rounded-lg border border-border p-2">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <input
                  className={`${inputCls} max-w-[240px]`}
                  placeholder="Search name or number"
                  value={pickQuery}
                  onChange={(e) => setPickQuery(e.target.value)}
                />
                <button
                  type="button"
                  className="text-[11.5px] text-accent hover:underline"
                  onClick={() =>
                    setPicked((prev) => {
                      const next = new Set(prev);
                      filteredPeople.forEach((p) => next.add(d10(p.phone)));
                      return next;
                    })
                  }
                >
                  Select shown
                </button>
                <button
                  type="button"
                  className="text-[11.5px] text-mutedDim hover:text-white"
                  onClick={() => setPicked(new Set())}
                >
                  Clear
                </button>
              </div>
              <div className="max-h-[260px] overflow-y-auto">
                {filteredPeople.map((p) => {
                  const k = d10(p.phone);
                  return (
                    <label
                      key={k}
                      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12.5px] hover:bg-white/5"
                    >
                      <input
                        type="checkbox"
                        checked={picked.has(k)}
                        onChange={() =>
                          setPicked((prev) => {
                            const next = new Set(prev);
                            if (next.has(k)) next.delete(k);
                            else next.add(k);
                            return next;
                          })
                        }
                      />
                      <span className="text-white">{p.name}</span>
                      <span className="tabular-nums text-mutedDim">…{k.slice(-4)}</span>
                      <span className="text-[10.5px] text-mutedDim">{p.tag}</span>
                    </label>
                  );
                })}
                {filteredPeople.length === 0 ? (
                  <div className="px-1.5 py-2 text-[12px] text-mutedDim">No one matches.</div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {/* WHEN */}
        <div>
          <label className={labelCls}>When</label>
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                ['now', 'Send now'],
                ['later', 'Schedule'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setWhen(k)}
                className={`rounded-full border px-3 py-1.5 text-[12px] font-medium ${
                  when === k ? 'border-accent bg-accent/15 text-accent' : 'border-border text-mutedDim hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
            {when === 'later' ? (
              <input
                type="datetime-local"
                className={`${inputCls} max-w-[230px]`}
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
              />
            ) : null}
          </div>
        </div>

        {/* WHAT */}
        <div>
          <label className={labelCls}>Message</label>
          <textarea
            className={`${inputCls} min-h-[96px]`}
            placeholder="e.g. Shado: Schedule change for SHADO AFTER DARK — doors now open at 9:30pm at Verve. See you Thursday!"
            value={message}
            maxLength={600}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="mt-1 text-[11px] text-mutedDim">
            {message.length}/600 · {segments} text{segments === 1 ? '' : 's'} each
            {ucs2 ? ' (emoji/special characters shorten each text)' : ''} · "Reply STOP to opt out."
            is added automatically
          </div>
          {message.trim().length >= 2 ? (
            <div className="mt-2 max-w-[360px] whitespace-pre-wrap rounded-2xl rounded-bl-md bg-[#26252a] px-3.5 py-2.5 text-[13px] leading-snug text-white">
              {finalText}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3">
          <span className="text-[12px] text-mutedDim">
            {recipientCount !== null
              ? `${recipientCount} ${recipientCount === 1 ? 'person' : 'people'} · ≈$${(
                  recipientCount * segments * 0.0083
                ).toFixed(2)}`
              : 'Counting…'}
          </span>
          <button
            type="button"
            className="admin-btn admin-btn-primary admin-btn-sm"
            disabled={busy || message.trim().length < 2 || recipientCount === 0}
            onClick={() => void submit()}
          >
            {busy ? 'Working…' : when === 'now' ? 'Send text' : 'Schedule text'}
          </button>
        </div>
        {notice ? <div className="text-[12.5px] text-accent">{notice}</div> : null}
      </div>

      {/* HISTORY */}
      <div className="admin-card space-y-2">
        <div className="admin-eyebrow">History{blasts ? ` — ${blasts.length}` : ''}</div>
        {blasts && blasts.length === 0 ? (
          <div className="text-[12.5px] text-mutedDim">No texts yet.</div>
        ) : null}
        {(blasts ?? []).map((b) => {
          const tone =
            b.status === 'sent'
              ? 'bg-emerald-500/15 text-emerald-300'
              : b.status === 'scheduled' || b.status === 'sending'
                ? 'bg-accent/15 text-accent'
                : b.status === 'failed'
                  ? 'bg-red-500/15 text-red-300'
                  : 'bg-white/5 text-mutedDim';
          const who =
            b.audience === 'custom'
              ? `${b.phones?.length ?? 0} picked`
              : AUDIENCE_LABELS.find((a) => a[0] === b.audience)?.[1] ?? b.audience;
          return (
            <div key={b.id} className="border-t border-border/60 pt-2 text-[12.5px]">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${tone}`}>{b.status}</span>
                <span className="text-white">{who}</span>
                <span className="text-mutedDim">
                  {b.status === 'scheduled'
                    ? `goes out ${new Date(b.send_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
                    : timeAgo(b.send_at)}
                </span>
                {b.sent_count !== null ? (
                  <span className="tabular-nums text-mutedDim">
                    {b.sent_count}/{b.recipient_count} delivered to Twilio
                    {b.failed_count ? <span className="text-red-300"> · {b.failed_count} failed</span> : null}
                  </span>
                ) : null}
                {b.status === 'scheduled' ? (
                  <button
                    type="button"
                    className="ml-auto text-[11px] font-medium text-red-400 hover:underline"
                    onClick={() => void cancel(b)}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
              <div className="mt-1 whitespace-pre-wrap text-mutedDim">{b.message}</div>
              {b.error ? <div className="mt-1 text-[11px] text-red-300">{b.error}</div> : null}
            </div>
          );
        })}
      </div>
    </>
  );
}
